// src/app/core/services/pre-read.service.ts


import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PreRead, PreReadListItem, QuestionAnswerPayload } from '../models/pre-read.model';

export interface QuestionDefinition {
  question_code: string;
  step: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class PreReadService {

  // Single plural base for every pre-read endpoint (GET/POST/PATCH/DELETE).
  private readonly base = `${environment.apiUrl}consumer/v3/prereads`;
  private readonly questionsUrl = `${environment.apiUrl}consumer/v3/questions`;

  constructor(private http: HttpClient) { }

  /** GET /consumer/v3/questions — question definitions, used to drive the
   *  step order/count dynamically instead of a hardcoded question list. */
  getQuestions(): Observable<QuestionDefinition[]> {
    return this.http.get<any>(this.questionsUrl).pipe(
      map(res => res?.data ?? res?.questions ?? res ?? [])
    );
  }

  /**
   * GET /consumer/v3/prereads — list all Legal Information entries for the
   * current user. Response shape: { error, pre_reads: [...] }.
   */
  list(): Observable<PreReadListItem[]> {
    return this.http.get<any>(this.base).pipe(
      map(res => res?.pre_reads ?? res?.data ?? [])
    );
  }

  /** GET /consumer/v3/prereads/:id — fetch a single entry (resume a draft or view a completed one) */
  getById(preReadId: string): Observable<PreRead> {
    return this.http.get<any>(`${this.base}/${preReadId}`).pipe(
      map(res => res?.data ?? res?.pre_read ?? res)
    );
  }

  /**
   * POST /consumer/v3/prereads — create a new draft.
   * Only called from an explicit user save action ("Save for later" /
   * "Save & Next") — never on dialog open — so opening the Legal
   * Information flow never creates an empty draft record.
   * Response: { error, msg, pre_read: { pre_read_id, status, current_step } }
   */
  createDraft(initialAnswers?: QuestionAnswerPayload[]): Observable<{ pre_read_id: string; status: string; current_step?: number }> {
    const payload = initialAnswers && initialAnswers.length ? { answers: initialAnswers } : {};
    return this.http.post<any>(this.base, payload).pipe(
      map(res => res?.pre_read ?? res?.data ?? res)
    );
  }

  /**
   * PATCH /consumer/v3/prereads/:id — save answer(s) for a step.
   * Body is { current_step, answers }. Callers pass the BACKEND
   * current_step, which is always the same as the UI step number
   * (see toBackendCurrentStep() in pre-read.model.ts).
   */
  patchAnswers(preReadId: string, backendCurrentStep: number, answers: QuestionAnswerPayload[]): Observable<any> {
    return this.http.patch<any>(`${this.base}/${preReadId}`, {
      current_step: backendCurrentStep,
      answers
    });
  }

  /** POST /consumer/v3/prereads/:id/submit — finalize the Legal Information entry */
  submit(preReadId: string): Observable<any> {
    return this.http.post<any>(`${this.base}/${preReadId}/submit`, {});
  }

  /** DELETE /consumer/v3/prereads/:id — remove a Draft entry only */
  deleteDraft(preReadId: string): Observable<any> {
    return this.http.delete<any>(`${this.base}/${preReadId}`);
  }

  /** POST /consumer/v3/prereads/:id/share — share with a lawyer */
  shareWithLawyer(preReadId: string, lawyerUid: string): Observable<any> {
    return this.http.post<any>(`${this.base}/${preReadId}/share`, { lawyer_uid: lawyerUid });
  }

  /** DELETE /consumer/v3/prereads/:id/share/:lawyerUid — revoke a lawyer's access */
  revokeAccess(preReadId: string, lawyerUid: string): Observable<any> {
    return this.http.delete<any>(`${this.base}/${preReadId}/share/${lawyerUid}`);
  }

 
  uploadDocuments(preReadId: string, files: File[], category: string): Observable<{ progress: number; done: boolean; response?: any }> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f, f.name));
    formData.append('category', category);

    return this.http.post<any>(`${this.base}/${preReadId}/documents`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          return { progress: Math.round((event.loaded / event.total) * 100), done: false };
        } else if (event.type === HttpEventType.Response) {
          return { progress: 100, done: true, response: event.body };
        }
        return { progress: 0, done: false };
      }),
      filter(evt => !!evt)
    );
  }
}